import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  DayCellContentArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import {
  IconCalendarPlus,
  IconInfoCircle,
  IconTrash,
} from '@tabler/icons-react';
import PlannerCalendar from '@/components/PlannerCalendar';
import { BackLink } from '@/components/BackLink';
import { useAuth } from '@/hooks/useAuth';
import type { AvailabilityStatus, ShiftArea, ShiftSlot } from '@/api/shift';
import {
  useAdminDeleteShiftRegistration,
  useCancelShift,
  useRegisterShift,
  useShiftAreas,
  useShiftSlots,
  useUpdateShiftSlot,
} from '@/hooks/useShift';

type PlannerMode = 'register' | 'edit';
type StaffingLevel = 'red' | 'yellow' | 'green';

const DEMO_AREAS: ShiftArea[] = [
  {
    id: 0,
    name: 'Lebensmittelzubereitung',
    karma: null,
    blockedWeekdays: ['TUESDAY', 'WEDNESDAY', 'THURSDAY'],
  },
  { id: 1, name: 'Lebensmittelverteilung', karma: null, blockedWeekdays: [] },
  {
    id: 2,
    name: 'ZoHus 14:50–17:30',
    karma: null,
    blockedWeekdays: ['SUNDAY'],
  },
  {
    id: 3,
    name: 'Kleiderkammer 15:00–17:30',
    karma: null,
    blockedWeekdays: ['SUNDAY'],
  },
  {
    id: 4,
    name: 'ZoHus 17:30–20:00',
    karma: null,
    blockedWeekdays: ['SUNDAY'],
  },
  {
    id: 5,
    name: 'Kleiderkammer 17:30–19:30',
    karma: null,
    blockedWeekdays: ['SUNDAY'],
  },
];

const DEFAULT_TIMES: Record<number, { start: string; end: string }> = {
  0: { start: '14:00', end: '16:30' },
  1: { start: '17:00', end: '20:00' },
  2: { start: '14:50', end: '17:30' },
  3: { start: '15:00', end: '17:30' },
  4: { start: '17:30', end: '20:00' },
  5: { start: '17:30', end: '19:30' },
};

function getStaffingLevel(areaId: number, count: number): StaffingLevel {
  if (areaId === 1) {
    if (count <= 1) return 'red';
    if (count === 2) return 'yellow';
    return 'green';
  }

  if (areaId === 2 || areaId === 4) {
    return count <= 1 ? 'red' : 'green';
  }

  if (count === 0) return 'red';
  if (count === 1) return 'yellow';
  return 'green';
}

function staffingBadgeColor(level: StaffingLevel): string {
  if (level === 'red') return 'red';
  if (level === 'yellow') return 'yellow';
  return 'green';
}

function getMobileAreaLabel(area: ShiftArea): string {
  const times = DEFAULT_TIMES[area.id];
  if (!times) return area.name;

  const baseName = area.name.replace(/\s+\d{2}:\d{2}–\d{2}:\d{2}$/, '');
  return `${times.start}–${times.end} · ${baseName}`;
}

function formatDateBerlin(date: Date): string {
  return date.toLocaleDateString('sv-SE', {
    timeZone: 'Europe/Berlin',
  });
}

function formatCalendarDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function createIcsTimestamp(date: string, time: string): string {
  return `${date.replace(/-/g, '')}T${time.replace(':', '')}00`;
}

function createIcsFile(area: ShiftArea, date: string): string | null {
  const times = DEFAULT_TIMES[area.id];
  if (!times) return null;

  const now = new Date()
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
  const title = `Straßenwächter – ${area.name}`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Strassenwaechter//Ehrenamtsportal//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:shift-${area.id}-${date}@strassenwaechter.org`,
    `DTSTAMP:${now}`,
    `DTSTART;TZID=Europe/Berlin:${createIcsTimestamp(date, times.start)}`,
    `DTEND;TZID=Europe/Berlin:${createIcsTimestamp(date, times.end)}`,
    `SUMMARY:${escapeIcsText(title)}`,
    `DESCRIPTION:${escapeIcsText('Ehrenamtliche Schicht bei Straßenwächter e.V.')}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

function createDemoSlots(): ShiftSlot[] {
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  return DEMO_AREAS.flatMap(area =>
    Array.from({ length: 12 }, (_, index) => {
      const date = addDays(today, index * 3 + 1);
      const dateString = formatDateBerlin(date);

      return {
        id: `demo-${area.id}-${dateString}`,
        date: dateString,
        areaId: area.id,
        display: index !== 5,
        placeholderCount: index === 2 ? 1 : 0,
        placeholderNames: [],
        registrations:
          index === 0
            ? [
                {
                  volunteerId: 'demo-volunteer',
                  displayName: 'Beispiel N.',
                  status: 'attends' as const,
                },
              ]
            : [],
      };
    })
  );
}

function statusLabel(status?: AvailabilityStatus): string {
  if (status === 'attends') return 'Eingetragen';
  if (status === 'supports') return 'Unterstützung';
  return 'Frei';
}

function formatMobileDate(date: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(`${date}T12:00:00`));
}

const CANCELLATION_BLOCKED_MESSAGE =
  'Cancellations are blocked within one day of the shift';

function getErrorMessage(error: unknown, t: TFunction): string {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    const backendMessage = error.response?.data?.message;
    if (backendMessage === CANCELLATION_BLOCKED_MESSAGE) {
      return t('shiftPlanner.notifications.cancellationBlocked');
    }
    return backendMessage ?? t('shiftPlanner.notifications.genericError');
  }
  return t('shiftPlanner.notifications.genericError');
}

export default function ShiftPlanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPreview = !user;
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.permissions?.includes('admin:manage-volunteers');
  const currentVolunteerId = user?.volunteerId ?? 'current-volunteer';
  const currentDisplayName =
    user?.volunteerName ?? user?.name ?? user?.email ?? 'Ich';

  const [selectedAreaId, setSelectedAreaId] = useState(0);
  const [mode, setMode] = useState<PlannerMode>('register');
  const [demoSlots, setDemoSlots] = useState<ShiftSlot[]>(createDemoSlots);
  const [editedSlotDate, setEditedSlotDate] = useState<string | null>(null);
  const [calendarPrompt, setCalendarPrompt] = useState<{
    date: string;
    area: ShiftArea;
  } | null>(null);
  const [editedSlotBlocked, setEditedSlotBlocked] = useState(false);
  const [editedPlaceholderCount, setEditedPlaceholderCount] = useState(0);
  const [editedPlaceholderNames, setEditedPlaceholderNames] = useState<
    string[]
  >([]);

  const range = useMemo(
    () => ({
      start: formatDateBerlin(new Date()),
      end: formatDateBerlin(addDays(new Date(), isAdmin ? 365 : 31)),
    }),
    [isAdmin]
  );

  const areasQuery = useShiftAreas(!isPreview);
  const availableAreas = isPreview ? DEMO_AREAS : (areasQuery.data ?? []);
  const hasSelectedArea = availableAreas.some(
    area => area.id === selectedAreaId
  );
  const slotsQuery = useShiftSlots(
    selectedAreaId,
    range.start,
    range.end,
    !isPreview && hasSelectedArea
  );
  const allSlots = isPreview ? demoSlots : (slotsQuery.data ?? []);

  const registerMutation = useRegisterShift();
  const cancelMutation = useCancelShift();
  const updateSlotMutation = useUpdateShiftSlot();
  const deleteRegistrationMutation = useAdminDeleteShiftRegistration();

  useEffect(() => {
    if (
      availableAreas.length > 0 &&
      !availableAreas.some(area => area.id === selectedAreaId)
    ) {
      setSelectedAreaId(availableAreas[0].id);
    }
  }, [availableAreas, selectedAreaId]);

  const selectedSlots = useMemo(
    () =>
      allSlots
        .filter(slot => slot.areaId === selectedAreaId)
        .sort((left, right) => left.date.localeCompare(right.date)),
    [allSlots, selectedAreaId]
  );

  const updateDemoSlot = useCallback(
    (slotIdOrDate: string, update: (slot: ShiftSlot) => ShiftSlot) => {
      setDemoSlots(current =>
        current.map(slot =>
          slot.id === slotIdOrDate || slot.date === slotIdOrDate
            ? update(slot)
            : slot
        )
      );
    },
    []
  );

  const showError = useCallback(
    (error: unknown) => {
      notifications.show({
        title: t('shiftPlanner.notifications.title'),
        message: getErrorMessage(error, t),
        color: 'red',
      });
    },
    [t]
  );

  const openSlotEditor = useCallback((slot: ShiftSlot) => {
    setEditedSlotDate(slot.date);
    setEditedSlotBlocked(!slot.display);
    setEditedPlaceholderCount(slot.placeholderCount);
    setEditedPlaceholderNames(
      Array.from(
        { length: slot.placeholderCount },
        (_, index) => slot.placeholderNames?.[index] ?? ''
      )
    );
  }, []);

  const closeSlotEditor = useCallback(() => {
    setEditedSlotDate(null);
  }, []);

  const saveSlotChanges = useCallback(async () => {
    if (!editedSlotDate) return;

    const update = {
      display: !editedSlotBlocked,
      placeholderCount: editedSlotBlocked ? 0 : editedPlaceholderCount,
      placeholderNames: editedSlotBlocked
        ? []
        : Array.from(
            { length: editedPlaceholderCount },
            (_, index) => editedPlaceholderNames[index]?.trim() ?? ''
          ),
    };

    try {
      if (isPreview) {
        updateDemoSlot(editedSlotDate, slot => ({ ...slot, ...update }));
      } else {
        await updateSlotMutation.mutateAsync({
          areaId: selectedAreaId,
          date: editedSlotDate,
          update,
        });
      }
      closeSlotEditor();
    } catch (error: unknown) {
      showError(error);
    }
  }, [
    closeSlotEditor,
    editedPlaceholderCount,
    editedPlaceholderNames,
    editedSlotBlocked,
    editedSlotDate,
    isPreview,
    selectedAreaId,
    showError,
    updateDemoSlot,
    updateSlotMutation,
  ]);

  const handleSlotAction = useCallback(
    async (date: string) => {
      const slot = selectedSlots.find(item => item.date === date);
      if (!slot) return;

      if (isAdmin && mode === 'edit') {
        openSlotEditor(slot);
        return;
      }

      if (!isPreview) {
        try {
          const isRegistered = slot.registrations.some(
            registration => registration.volunteerId === currentVolunteerId
          );

          if (isRegistered) {
            await cancelMutation.mutateAsync({
              areaId: selectedAreaId,
              date: slot.date,
            });
          } else {
            await registerMutation.mutateAsync({
              areaId: selectedAreaId,
              date: slot.date,
            });
            const selectedArea = availableAreas.find(
              area => area.id === selectedAreaId
            );
            if (selectedArea && DEFAULT_TIMES[selectedArea.id]) {
              setCalendarPrompt({ date: slot.date, area: selectedArea });
            }
          }
        } catch (error: unknown) {
          showError(error);
        }
        return;
      }

      updateDemoSlot(slot.id ?? slot.date, currentSlot => {
        if (!currentSlot.display) return currentSlot;
        const isRegistered = currentSlot.registrations.some(
          registration => registration.volunteerId === currentVolunteerId
        );

        return {
          ...currentSlot,
          registrations: isRegistered
            ? currentSlot.registrations.filter(
                registration => registration.volunteerId !== currentVolunteerId
              )
            : [
                ...currentSlot.registrations,
                {
                  volunteerId: currentVolunteerId,
                  displayName: currentDisplayName,
                  status: 'attends',
                },
              ],
        };
      });
    },
    [
      cancelMutation,
      availableAreas,
      currentDisplayName,
      currentVolunteerId,
      isAdmin,
      isPreview,
      mode,
      openSlotEditor,
      registerMutation,
      selectedAreaId,
      selectedSlots,
      showError,
      updateDemoSlot,
    ]
  );

  const downloadCalendarEvent = useCallback(() => {
    if (!calendarPrompt) return;

    const contents = createIcsFile(calendarPrompt.area, calendarPrompt.date);
    if (!contents) return;

    const blob = new Blob([contents], {
      type: 'text/calendar;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `strassenwaechter-${calendarPrompt.date}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setCalendarPrompt(null);
  }, [calendarPrompt]);

  const removeRegistration = useCallback(
    async (date: string, volunteerId: string) => {
      if (!isPreview) {
        try {
          await deleteRegistrationMutation.mutateAsync({
            areaId: selectedAreaId,
            date,
            volunteerId,
          });
        } catch (error: unknown) {
          showError(error);
        }
        return;
      }

      updateDemoSlot(date, slot => ({
        ...slot,
        registrations: slot.registrations.filter(
          registration => registration.volunteerId !== volunteerId
        ),
      }));
    },
    [
      deleteRegistrationMutation,
      isPreview,
      selectedAreaId,
      showError,
      updateDemoSlot,
    ]
  );

  const events = useMemo<EventInput[]>(
    () =>
      selectedSlots.flatMap(slot => [
        ...slot.registrations.map(registration => ({
          id: `${slot.date}-${registration.volunteerId}`,
          title: registration.displayName,
          start: slot.date,
          allDay: true,
          backgroundColor: registration.status === 'attends' ? 'green' : 'gold',
          borderColor:
            registration.status === 'attends' ? 'darkgreen' : 'darkgoldenrod',
          extendedProps: {
            date: slot.date,
            volunteerId: registration.volunteerId,
            status: registration.status,
          },
        })),
        ...Array.from({ length: slot.placeholderCount }, (_, index) => ({
          id: `${slot.date}-placeholder-${index + 1}`,
          title:
            slot.placeholderNames?.[index]?.trim() || `Anfänger ${index + 1}`,
          start: slot.date,
          allDay: true,
          backgroundColor: '#e5e7eb',
          borderColor: '#9ca3af',
          textColor: '#495057',
          extendedProps: {
            date: slot.date,
            placeholder: true,
          },
        })),
      ]),
    [selectedSlots]
  );

  const handleDateClick = useCallback(
    (arg: DateClickArg) => {
      const slot = selectedSlots.find(item => item.date === arg.dateStr);
      if (slot) void handleSlotAction(slot.date);
    },
    [handleSlotAction, selectedSlots]
  );

  const handleEventClick = useCallback(
    async (arg: EventClickArg) => {
      if (!isAdmin || mode !== 'edit') return;

      const date = arg.event.extendedProps.date as string | undefined;
      if (!date) return;

      if (arg.event.extendedProps.placeholder) {
        const slot = selectedSlots.find(item => item.date === date);
        if (!slot) return;
        openSlotEditor(slot);
        return;
      }

      const volunteerId = arg.event.extendedProps.volunteerId as
        | string
        | undefined;
      if (volunteerId) {
        await removeRegistration(date, volunteerId);
      }
    },
    [isAdmin, mode, openSlotEditor, removeRegistration, selectedSlots]
  );

  const renderDayCell = useCallback(
    ({ date, dayNumberText }: DayCellContentArg) => {
      const dateString = formatCalendarDateKey(date);
      const slot = selectedSlots.find(item => item.date === dateString);
      const ownRegistration = slot?.registrations.find(
        registration => registration.volunteerId === currentVolunteerId
      );
      const staffingLevel = slot
        ? getStaffingLevel(slot.areaId, slot.registrations.length)
        : null;

      return (
        <div className="shift-planner-day">
          {slot?.display && staffingLevel ? (
            <span
              className={`shift-planner-day__staffing shift-planner-day__staffing--${staffingLevel}`}
            />
          ) : null}
          <span className="shift-planner-day__number">{dayNumberText}</span>
          {ownRegistration ? (
            <span
              className="shift-planner-day__own-registration"
              aria-label={t('shiftPlanner.staffing.ownRegistration')}
            >
              ✓
            </span>
          ) : null}
          {slot && !slot.display ? (
            <span className="shift-planner-day__blocked">×</span>
          ) : null}
        </div>
      );
    },
    [currentVolunteerId, selectedSlots, t]
  );

  const validRange = useCallback(
    (now: Date) => ({
      start: formatDateBerlin(now),
      end: formatDateBerlin(addDays(now, isAdmin ? 365 : 31)),
    }),
    [isAdmin]
  );

  const selectedTimes = DEFAULT_TIMES[selectedAreaId];
  const editedSlot = editedSlotDate
    ? selectedSlots.find(slot => slot.date === editedSlotDate)
    : undefined;
  const isLoading =
    !isPreview && (areasQuery.isLoading || slotsQuery.isLoading);
  const hasLoadError = !isPreview && (areasQuery.isError || slotsQuery.isError);
  const mutationPending =
    registerMutation.isPending ||
    cancelMutation.isPending ||
    updateSlotMutation.isPending ||
    deleteRegistrationMutation.isPending;

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {user ? <BackLink to="/app" /> : null}

        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={1}>Schichtplan</Title>
            <Text c="dimmed">
              Verfügbarkeiten und Besetzung nach Arbeitsbereich
            </Text>
          </div>
          {isPreview ? (
            <Badge color="yellow" variant="light" size="lg">
              Frontend-Vorschau
            </Badge>
          ) : null}
        </Group>

        {isPreview ? (
          <Alert
            icon={<IconInfoCircle size={18} />}
            title="Öffentliche Vorschau"
            color="blue"
          >
            Diese Vorschau verwendet lokale Beispieldaten. Nach der Anmeldung
            wird der echte Schichtplan aus der Datenbank geladen.
          </Alert>
        ) : null}

        {hasLoadError ? (
          <Alert color="red" title="Schichtplan konnte nicht geladen werden">
            Bitte prüfe, ob das Backend läuft, und lade die Seite erneut.
          </Alert>
        ) : null}

        {!isPreview && availableAreas.length === 0 ? (
          <Alert color="yellow" title="Keine Bereiche zugeordnet">
            Für dein Benutzerkonto wurden noch keine Schichtplan-Bereiche
            freigeschaltet.
          </Alert>
        ) : null}

        <Card withBorder radius="md" padding="lg">
          <Group justify="space-between" align="flex-end">
            <Select
              label="Bereich"
              data={availableAreas.map(area => ({
                value: String(area.id),
                label: isMobile ? getMobileAreaLabel(area) : area.name,
              }))}
              value={String(selectedAreaId)}
              onChange={value => {
                if (value !== null) setSelectedAreaId(Number(value));
              }}
              allowDeselect={false}
              disabled={availableAreas.length === 0}
              w={{ base: '100%', sm: 320 }}
            />

            {isAdmin ? (
              <SegmentedControl
                value={mode}
                onChange={value => setMode(value as PlannerMode)}
                data={[
                  { value: 'register', label: 'Eintragen' },
                  { value: 'edit', label: 'Bearbeiten' },
                ]}
              />
            ) : null}
          </Group>
          <Group gap="md" mt="md">
            {(['red', 'yellow', 'green'] as const).map(level => (
              <Group gap={6} key={level}>
                <span
                  className={`shift-planner-legend shift-planner-legend--${level}`}
                />
                <Text size="xs" c="dimmed">
                  {t(`shiftPlanner.staffing.${level}`)}
                </Text>
              </Group>
            ))}
          </Group>
        </Card>

        {isMobile ? (
          <Stack gap="sm">
            {selectedSlots.map(slot => {
              const ownRegistration = slot.registrations.find(
                registration => registration.volunteerId === currentVolunteerId
              );
              const staffingLevel = getStaffingLevel(
                slot.areaId,
                slot.registrations.length
              );
              const actionLabel =
                isAdmin && mode === 'edit'
                  ? 'Bearbeiten'
                  : ownRegistration
                    ? 'Austragen'
                    : 'Eintragen';

              return (
                <Card
                  key={`${slot.areaId}-${slot.date}`}
                  withBorder
                  radius="md"
                  padding="md"
                >
                  <Stack gap="sm">
                    <Group justify="space-between" align="flex-start">
                      <div>
                        <Text fw={600}>{formatMobileDate(slot.date)}</Text>
                        <Text size="sm" c="dimmed">
                          {slot.date}
                          {selectedTimes
                            ? ` · ${selectedTimes.start}–${selectedTimes.end}`
                            : ''}
                        </Text>
                      </div>
                      <Stack gap={4} align="flex-end">
                        <Badge
                          color={
                            slot.display
                              ? staffingBadgeColor(staffingLevel)
                              : 'gray'
                          }
                        >
                          {slot.display
                            ? t('shiftPlanner.staffing.count', {
                                count: slot.registrations.length,
                              })
                            : 'Gesperrt'}
                        </Badge>
                        {slot.display ? (
                          <Text size="xs" c="dimmed">
                            {statusLabel(ownRegistration?.status)}
                          </Text>
                        ) : null}
                      </Stack>
                    </Group>

                    {slot.registrations.length === 0 &&
                    slot.placeholderCount === 0 ? (
                      <Text size="sm" c="dimmed">
                        Noch niemand eingetragen
                      </Text>
                    ) : (
                      <Stack gap={6}>
                        {slot.registrations.map(registration => (
                          <Group
                            key={registration.volunteerId}
                            justify="space-between"
                          >
                            <Text size="sm">{registration.displayName}</Text>
                            {isAdmin && mode === 'edit' ? (
                              <Button
                                size="compact-xs"
                                color="red"
                                variant="subtle"
                                leftSection={<IconTrash size={14} />}
                                onClick={() =>
                                  void removeRegistration(
                                    slot.date,
                                    registration.volunteerId
                                  )
                                }
                              >
                                Löschen
                              </Button>
                            ) : null}
                          </Group>
                        ))}
                        {Array.from(
                          { length: slot.placeholderCount },
                          (_, index) => (
                            <Text key={index} size="sm" c="dimmed">
                              {slot.placeholderNames?.[index]?.trim() ||
                                `Anfänger ${index + 1}`}
                            </Text>
                          )
                        )}
                      </Stack>
                    )}

                    <Button
                      color={
                        mode === 'edit'
                          ? 'blue'
                          : ownRegistration
                            ? 'red'
                            : 'green'
                      }
                      variant={
                        mode === 'edit'
                          ? 'light'
                          : ownRegistration
                            ? 'light'
                            : 'filled'
                      }
                      loading={mutationPending}
                      disabled={!slot.display && mode === 'register'}
                      onClick={() => void handleSlotAction(slot.date)}
                    >
                      {actionLabel}
                    </Button>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Card withBorder radius="md" padding="lg">
            <PlannerCalendar
              events={events}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              dayCellContent={renderDayCell}
              validRange={validRange}
            />
          </Card>
        )}
      </Stack>

      <Modal
        opened={calendarPrompt !== null}
        onClose={() => setCalendarPrompt(null)}
        title={t('shiftPlanner.calendarDialog.title')}
        centered
      >
        <Stack>
          <Text>
            {t('shiftPlanner.calendarDialog.message', {
              area: calendarPrompt?.area.name,
              date: calendarPrompt
                ? formatMobileDate(calendarPrompt.date)
                : undefined,
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('shiftPlanner.calendarDialog.details')}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCalendarPrompt(null)}>
              {t('shiftPlanner.calendarDialog.close')}
            </Button>
            <Button
              leftSection={<IconCalendarPlus size={16} />}
              onClick={downloadCalendarEvent}
            >
              {t('shiftPlanner.calendarDialog.add')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={editedSlotDate !== null}
        onClose={closeSlotEditor}
        title="Datum bearbeiten"
        centered
      >
        <Stack gap="lg">
          <div>
            <Text fw={600}>
              {editedSlotDate ? formatMobileDate(editedSlotDate) : ''}
            </Text>
            <Text size="sm" c="dimmed">
              {availableAreas.find(area => area.id === selectedAreaId)?.name}
              {editedSlotDate ? ` · ${editedSlotDate}` : ''}
            </Text>
          </div>

          <Switch
            label="Datum blockieren"
            description="An diesem Datum können sich keine Ehrenamtlichen eintragen."
            checked={editedSlotBlocked}
            onChange={event =>
              setEditedSlotBlocked(event.currentTarget.checked)
            }
          />

          {editedSlotBlocked && (editedSlot?.registrations.length ?? 0) > 0 ? (
            <Alert color="yellow" title="Bereits eingetragene Personen">
              Vorhandene Eintragungen bleiben erhalten. Entferne sie bei Bedarf
              separat aus dem Schichtplan.
            </Alert>
          ) : null}

          <div>
            <Text size="sm" fw={500} mb="xs">
              Anfängerplätze
            </Text>
            <SegmentedControl
              fullWidth
              value={String(editedPlaceholderCount)}
              onChange={value => setEditedPlaceholderCount(Number(value))}
              data={[
                { value: '0', label: 'Keine' },
                { value: '1', label: '1 Anfänger' },
                { value: '2', label: '2 Anfänger' },
              ]}
              disabled={editedSlotBlocked}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Mit „Keine“ werden vorhandene Anfängerplätze entfernt. Maximal
              zwei Anfängerplätze sind möglich.
            </Text>
          </div>

          {!editedSlotBlocked && editedPlaceholderCount > 0 ? (
            <Stack gap="sm">
              {Array.from({ length: editedPlaceholderCount }, (_, index) => (
                <TextInput
                  key={index}
                  label={`Name Anfänger ${index + 1} (optional)`}
                  placeholder={`Anfänger ${index + 1}`}
                  value={editedPlaceholderNames[index] ?? ''}
                  maxLength={80}
                  onChange={event => {
                    const value = event.currentTarget.value;
                    setEditedPlaceholderNames(current => {
                      const next = [...current];
                      next[index] = value;
                      return next;
                    });
                  }}
                />
              ))}
              <Text size="xs" c="dimmed">
                Ohne Namen wird weiterhin „Anfänger 1“ beziehungsweise „Anfänger
                2“ angezeigt.
              </Text>
            </Stack>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={closeSlotEditor}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void saveSlotChanges()}
              loading={updateSlotMutation.isPending}
            >
              Speichern
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
